import { useEffect, useRef, useState, useCallback, useMemo, FormEvent } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, Search, Clock, Sparkles, TrendingUp, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import GoKeycap from "@/components/GoKeycap";
import { useInstantSearchSeed } from "@/hooks/useInstantSearchSeed";
import { useAiSuggestionPool } from "@/hooks/useAiSuggestionPool";
import { useAuth } from "@/contexts/AuthContext";
import { normalizeQuery } from "@/lib/normalizeQuery";
import { fuzzyScore } from "@/lib/fuzzyMatch";
import { lockBodyScroll } from "@/lib/bodyScrollLock";

interface Suggestion {
  text: string;
  source: "log" | "ai";
}

// Unified row shape. Each entry knows where it came from so we can show the
// correct icon/colour without branching on "showLive" at render time.
type RowKind = "recent" | "top" | "log" | "ai";
interface ListRow {
  text: string;
  kind: RowKind;
  /** Lower = more relevant. Used only when filtering by typed query. */
  score?: number;
}

interface MobileSearchOverlayProps {
  open: boolean;
  initialQuery: string;
  onClose: () => void;
  onSubmit: (query: string) => void;
}

/**
 * Google-style fullscreen mobile search experience.
 *
 * Display contract (mirrors product spec):
 *   - On open with empty query:
 *       Signed in  → up to 7 recents, then trending (up to 10).
 *       Signed out → trending only (up to 10).
 *   - As the user types, list dynamically filters & re-orders to:
 *       1. Recents matching typed text (signed-in only)
 *       2. Trending matching typed text
 *       3. AI-suggested completions (precomputed pool, fuzzy-matched)
 *       4. Live log suggestions from suggest-queries (DB matches)
 *   - All sources de-duplicated by normalizeQuery() so no row appears twice.
 *
 * Speed:
 *   - Recents + trending preloaded by useInstantSearchSeed before overlay opens.
 *   - AI pool (top 50) preloaded by useAiSuggestionPool from the daily-refreshed
 *     ai_suggestion_cache table — instant fuzzy-filter, zero latency.
 *   - Live log suggestions still call suggest-queries with 500 ms debounce as
 *     a supplemental signal for queries we haven't pre-cached.
 */
export default function MobileSearchOverlay({
  open,
  initialQuery,
  onClose,
  onSubmit,
}: MobileSearchOverlayProps) {
  const { user } = useAuth();
  const [query, setQuery] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestError, setSuggestError] = useState(false);
  const [lastFetched, setLastFetched] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  // Forward-ref so the open-effect can call the latest fetchSuggestions
  // without creating a TDZ on the useCallback declared below.
  const fetchSuggestionsRef = useRef<((partial: string) => void) | null>(null);
  const seed = useInstantSearchSeed();
  const aiPool = useAiSuggestionPool();

  // Reset and focus when the overlay opens. If an initialQuery was injected
  // (e.g. from a trending topic chip), fetch suggestions for it immediately
  // so the experience matches typing the same string by hand.
  useEffect(() => {
    if (!open) return;
    setQuery(initialQuery);
    setSuggestions([]);
    const id = requestAnimationFrame(() => {
      inputRef.current?.focus();
      const el = inputRef.current;
      if (el && initialQuery) {
        try { el.setSelectionRange(initialQuery.length, initialQuery.length); } catch { /* noop */ }
      }
    });
    if (initialQuery && initialQuery.trim().length >= 2) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fetchSuggestionsRef.current?.(initialQuery), 50);
    }
    return () => cancelAnimationFrame(id);
  }, [open, initialQuery]);

  // Lock body scroll while overlay is open (refcounted so overlapping modals don't leak).
  useEffect(() => {
    if (!open) return;
    return lockBodyScroll();
  }, [open]);


  // ESC closes (desktop accessibility — mobile uses back arrow).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const fetchSuggestions = useCallback(async (partial: string) => {
    const trimmed = partial.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      setSuggestError(false);
      return;
    }
    setLoadingSuggestions(true);
    setSuggestError(false);
    setLastFetched(trimmed);
    try {
      const { data, error } = await supabase.functions.invoke("suggest-queries", {
        body: { partial: trimmed },
      });
      if (error) {
        setSuggestions([]);
        setSuggestError(true);
        return;
      }
      const source = data?.source || "logs";
      const items: Suggestion[] = (data?.suggestions || []).map((text: string, i: number) => ({
        text,
        source: source === "hybrid" && i >= (data?.suggestions?.length - 2) ? "ai" : "log",
      }));
      setSuggestions(items);
    } catch {
      setSuggestions([]);
      setSuggestError(true);
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  // Keep the ref in sync so the open-effect can call the latest impl.
  useEffect(() => { fetchSuggestionsRef.current = fetchSuggestions; }, [fetchSuggestions]);



  const handleChange = (value: string) => {
    setQuery(value);
    setSuggestError(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // 250 ms — tighter than the original 500 ms because the precomputed AI
    // pool already covers most prefixes instantly; the live fetch is just a
    // long-tail supplement and shouldn't feel laggy.
    debounceRef.current = setTimeout(() => fetchSuggestions(value), 250);
  };

  const handleRetry = () => fetchSuggestions(query);
  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    const q = query.trim();
    if (!q) return;
    onSubmit(q);
  };
  const handlePick = (text: string) => onSubmit(text);
  const handleClear = () => {
    setQuery("");
    setSuggestions([]);
    inputRef.current?.focus();
  };

  // Build the unified, deduped, ordered list shown to the user.
  const list = useMemo<ListRow[]>(() => {
    const typedKey = normalizeQuery(query);
    const isTyping = typedKey.length >= 2;

    const seen = new Set<string>();
    const tryPush = (target: ListRow[], text: string, kind: RowKind, score?: number) => {
      const key = normalizeQuery(text);
      if (!key || key === typedKey || seen.has(key)) return;
      seen.add(key);
      target.push({ text, kind, score });
    };

    // Partition seed (which is already in the right order) into recents/top.
    const seedRecents = seed.filter((s) => s.source === "recent");
    const seedTop = seed.filter((s) => s.source === "top");

    // ── Idle state: show seed exactly. ──────────────────────────────────────
    if (!isTyping) {
      const rows: ListRow[] = [];
      if (user?.id) for (const r of seedRecents) tryPush(rows, r.text, "recent");
      for (const t of seedTop) tryPush(rows, t.text, "top");
      return rows.slice(0, 17);
    }

    // ── Typing state: fuzzy-rank each source independently, then concat ────
    const matched = <T,>(items: T[], getText: (i: T) => string) =>
      items
        .map((it) => ({ it, score: fuzzyScore(query, getText(it)) }))
        .filter((x) => Number.isFinite(x.score))
        .sort((a, b) => a.score - b.score);

    const recentRows: ListRow[] = [];
    if (user?.id) {
      for (const m of matched(seedRecents, (s) => s.text)) {
        tryPush(recentRows, m.it.text, "recent", m.score);
      }
    }

    const topRows: ListRow[] = [];
    for (const m of matched(seedTop, (s) => s.text)) {
      tryPush(topRows, m.it.text, "top", m.score);
    }

    const aiRows: ListRow[] = [];
    for (const m of matched(aiPool, (s) => s)) {
      tryPush(aiRows, m.it, "ai", m.score);
    }

    // Live log suggestions (server) appended after the AI pool — they're a
    // long-tail signal for queries the precomputed cache didn't cover.
    const logRows: ListRow[] = [];
    for (const s of suggestions) {
      const kind: RowKind = s.source === "ai" ? "ai" : "log";
      tryPush(logRows, s.text, kind);
    }

    // Order: recents → trending → AI → live log.
    const merged = [...recentRows, ...topRows, ...aiRows, ...logRows];

    // Fallback: if the typed query (often a multi-word injected trending
    // topic) didn't fuzzy-match anything, surface the idle seed instead of
    // an empty list. This keeps the UX identical to manual typing — users
    // always see something pickable instead of "No suggestions yet".
    if (merged.length === 0) {
      const fallback: ListRow[] = [];
      if (user?.id) for (const r of seedRecents) tryPush(fallback, r.text, "recent");
      for (const t of seedTop) tryPush(fallback, t.text, "top");
      for (const a of aiPool.slice(0, 6)) tryPush(fallback, a, "ai");
      return fallback.slice(0, 12);
    }

    return merged.slice(0, 12);
  }, [query, seed, aiPool, suggestions, user?.id]);

  if (!open || typeof document === "undefined") return null;

  const isTyping = query.trim().length >= 2;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search ecommerce podcasts"
      className="fixed inset-0 z-[60] bg-background flex flex-col animate-fade-in"
    >
      {/* Top bar — back arrow + input + clear (Google-style header) */}
      <form
        onSubmit={handleSubmit}
        className="flex items-center gap-1 border-b-[3px] border-foreground bg-card px-2 py-2 flex-shrink-0"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close search and return to homepage"
          className="p-2 rounded-md hover:bg-muted active:bg-muted transition-colors flex-shrink-0"
        >
          <ArrowLeft className="w-5 h-5 text-foreground" />
        </button>
        <Search className="w-4 h-4 text-accent flex-shrink-0" aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          inputMode="search"
          enterKeyHint="search"
          autoCorrect="off"
          autoCapitalize="none"
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="Ask an ecommerce question…"
          className="flex-1 min-w-0 bg-transparent font-mono text-base leading-snug text-ink-blue placeholder:text-ink-blue-light placeholder:opacity-90 focus:outline-none px-2 py-2"
        />
        {query && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
            className="p-2 rounded-md hover:bg-muted active:bg-muted transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        )}
        <div className={`flex-shrink-0 ml-1 transition-all ${query.trim() ? "opacity-100 scale-100" : "opacity-0 scale-75 w-0 overflow-hidden pointer-events-none"}`}>
          <GoKeycap onClick={handleSubmit} size="compact" />
        </div>
      </form>

      {/* Scrollable suggestion area */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {/* Skeleton: only when truly empty AND something is loading. With
            preloaded seed + AI pool this almost never appears now. */}
        {loadingSuggestions && list.length === 0 && !suggestError && (
          <ul className="divide-y divide-border" aria-hidden="true">
            {Array.from({ length: 6 }).map((_, i) => (
              <li key={`skeleton-${i}`} className="px-4 py-3 flex items-center gap-3">
                <div className="w-4 h-4 rounded bg-muted animate-pulse flex-shrink-0" />
                <div
                  className="h-3 rounded bg-muted animate-pulse"
                  style={{ width: `${60 + ((i * 7) % 30)}%` }}
                />
              </li>
            ))}
          </ul>
        )}

        {suggestError && isTyping && (
          <div className="px-4 py-3 flex items-center justify-between gap-3 border-b border-border bg-muted/40">
            <span className="font-mono text-xs text-muted-foreground">
              Couldn't load live suggestions for "{lastFetched}".
            </span>
            <button
              type="button"
              onClick={handleRetry}
              className="font-mono text-xs font-bold text-accent underline underline-offset-2 hover:text-accent/80 active:text-accent/80 flex-shrink-0"
            >
              Retry
            </button>
          </div>
        )}

        {list.length === 0 && !loadingSuggestions && !suggestError && (
          <div className="px-4 py-8 text-center font-mono text-xs text-muted-foreground">
            {isTyping ? "No suggestions yet — keep typing or hit GO." : "Loading popular questions…"}
          </div>
        )}
        <ul className="divide-y divide-border">
          {list.map((item, i) => {
            const Icon =
              item.kind === "ai" ? Sparkles
              : item.kind === "recent" || item.kind === "log" ? Clock
              : TrendingUp;
            const iconClass =
              item.kind === "ai" ? "text-accent"
              : item.kind === "recent" || item.kind === "log" ? "text-muted-foreground"
              : "text-retro-amber";

            // Insert a subtle "Magical picks" section header above the first AI row.
            const isFirstAi = item.kind === "ai" && !list.slice(0, i).some((p) => p.kind === "ai");

            return (
              <li key={`${i}-${item.text}`}>
                {isFirstAi && (
                  <div
                    className="px-4 pt-3 pb-1 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-accent/80"
                    aria-hidden="true"
                  >
                    <Sparkles className="w-3 h-3" />
                    <span>Magical picks</span>
                    <span className="flex-1 ml-2 border-t border-dashed border-accent/30" />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => handlePick(item.text)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left font-mono text-sm hover:bg-muted active:bg-muted transition-colors ${
                    item.kind === "ai" ? "bg-accent/[0.04]" : ""
                  }`}
                >
                  <Icon className={`w-4 h-4 flex-shrink-0 ${iconClass}`} aria-hidden="true" />
                  <span className="flex-1 truncate text-ink-blue">{item.text}</span>
                  {item.kind === "ai" && (
                    <span className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-accent/10 border border-accent/30 font-mono text-[9px] uppercase tracking-wider text-accent">
                      <Sparkles className="w-2.5 h-2.5" />
                      AI
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
        {loadingSuggestions && isTyping && list.length > 0 && (
          <div className="px-4 py-3 text-[10px] font-mono text-muted-foreground">
            Finding more suggestions…
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
